import React from 'react';
import ReactMarkdown from 'react-markdown';
import Link from '@amzn/awsui-components-core/link';
import { Image } from '../Image/Image';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './QvpBanner.scss';

/**
 * QVP Banner - Quick Value Proposition banner component
 *
 * Displays concise value propositions in various layouts:
 * - Full width: Single centered text in a 1264-max-width wrapper
 * - Container: Same wrapper, with the text inside an off-white rounded pill
 * - Multi-column: 2/3/4 stats with optional icons and a heading
 * - Inline stat: Large orange number inline with descriptive text (52/40 Heading 400)
 *
 * Accessibility:
 * - Uses semantic HTML with proper ARIA labels and roles
 * - Keyboard navigable with visible focus indicators
 * - Screen reader friendly with descriptive regions and labels
 * - Icons marked as decorative with aria-hidden
 * - Stats have descriptive aria-labels for screen readers
 */

export type QvpBannerVariant = 'full-width' | 'container' | 'multi-column' | 'inline-stat';
export type QvpBannerBackground = 'white' | 'off-white';

/**
 * @polySemanticUsage About this block: a banner that highlights key metrics or statistics (e.g.
 * "300M+ customers", "60% average sales increase") to quantify the value proposition. Use it to
 * build credibility through data-driven proof points.
 * Variant selection is critical — choose the right one for the source layout:
 * • multi-column (DEFAULT, preferred): displays 2-4 stat items in a horizontal row. Each column
 *   has a stat number (column{N}Stat), descriptive text (column{N}Text), and optional icon
 *   (column{N}Icon). This is the standard "stats row" layout — use it whenever the source shows
 *   multiple statistics side by side.
 * • full-width: a single centered text statement spanning the full width. No stats, no columns.
 *   Use for a single impactful value-proposition sentence.
 * • container: same as full-width but the text sits inside a rounded off-white pill. Use for
 *   stylistic emphasis of a single statement.
 * • inline-stat: renders ONE large highlighted number (inlineStat) inline with descriptive text
 *   (text) — e.g. "300M+ unique visitors monthly". Do NOT use inline-stat for multiple
 *   statistics; it renders a single stat on the left in accent color with supporting text to its
 *   right. If the source shows multiple stats, use multi-column instead.
 */
export interface QvpBannerProps {
  /**
   * Banner variant
   * @polyLabel Banner Variant
   * @polyOptions Full Width=full-width,Container=container,Multi-Column=multi-column,Inline Stat=inline-stat
   */
  variant?: QvpBannerVariant;

  /**
   * Text content (for full-width, container, and inline-stat variants)
   * @polyLabel Banner Text
   */
  text?: string;

  /**
   * Number of columns to display. Values must have matching `columnN*`
   * slot props (2, 3, 4, or 5).
   * @polyType Text
   * @polyTranslatable false
   */
  columnCount?: 2 | 3 | 4 | 5;

  /**
   * Background style preset
   *
   * **Options**:
   * - `'white'`: White background (default for main components)
   * - `'off-white'`: Off-white background (default QVP style)
   * @polyOptions White=white,Off-White=off-white
   */
  background?: QvpBannerBackground;

  /** Optional heading displayed above multi-column stats (e.g. "Build your brand with us") */
  heading?: string;

  /**
   * Inline stat number for the 'inline-stat' variant (e.g. "300M+").
   * Rendered in accent color inline with the text.
   */
  inlineStat?: string;

  /** Column 1 */
  column1Stat?: string;
  column1Text?: string;
  /**
   * Glyph/emoji or icon URL rendered inside a 72×72 box. Pass a URL (http(s) or root-relative path) to an image/SVG asset (recommended), or a short glyph string.
   * @polyType Url
   */
  column1Icon?: string;
  column1StatLabel?: string;
  /** When set, the icon is exposed to assistive tech as `role="img"` with this text; otherwise it is treated as decorative (`aria-hidden`). */
  column1IconLabel?: string;

  /** Column 2 */
  column2Stat?: string;
  column2Text?: string;
  /** @polyType Url */
  column2Icon?: string;
  column2StatLabel?: string;
  column2IconLabel?: string;

  /** Column 3 */
  column3Stat?: string;
  column3Text?: string;
  /** @polyType Url */
  column3Icon?: string;
  column3StatLabel?: string;
  column3IconLabel?: string;

  /** Column 4 */
  column4Stat?: string;
  column4Text?: string;
  /** @polyType Url */
  column4Icon?: string;
  column4StatLabel?: string;
  column4IconLabel?: string;

  /** Column 5 */
  column5Stat?: string;
  column5Text?: string;
  /** @polyType Url */
  column5Icon?: string;
  column5StatLabel?: string;
  column5IconLabel?: string;

  /**
   * Accessible name for the banner region landmark. Authored and translatable
   * because the banner has no heading to name itself from, and a fixed
   * English name would be announced on every locale.
   * @polyLabel Screen Reader Label
   * @polyHelp Name screen readers announce for this banner. Translated with the page. Leave as "Value proposition" unless the banner needs a more specific name.
   * @polyDefault Value proposition
   * @polyTranslatable true
   */
  ariaLabel?: string;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

const DEFAULT_ARIA_LABEL = 'Value proposition';

/**
 * Maps background preset values to Poly design tokens
 */
const getBackgroundColor = (background?: QvpBannerBackground): string => {
  const backgroundMap: Record<QvpBannerBackground, string> = {
    'white': 'var(--poly-color-background-white)',
    'off-white': 'var(--poly-color-background-container-offWhite)',
  };

  if (!background) return backgroundMap['off-white'];

  const color = backgroundMap[background];
  if (!color) {
    console.warn(`[QvpBanner] Unknown background preset: "${background}". Falling back to "off-white".`);
    return backgroundMap['off-white'];
  }

  return color;
};

const RICH_TEXT_COMPONENTS = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <Link href={href ?? ''} variant="primary" fontSize="inherit">
      {children}
    </Link>
  ),
  p: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
};

const renderQvpText = (text: string, className: string): React.ReactNode => (
  <p className={className}>
    <ReactMarkdown components={RICH_TEXT_COMPONENTS}>{text}</ReactMarkdown>
  </p>
);

/**
 * Renders a column icon. A URL (http(s) or root-relative path) renders as a
 * decorative <Image>; any other value is treated as a glyph/emoji and rendered
 * as escaped text. Raw HTML is intentionally not rendered so an untrusted CMS
 * icon string can never inject markup — pass an SVG/image asset as a URL.
 */
const renderColumnIcon = (icon: string): React.ReactNode =>
  icon.startsWith('http') || icon.startsWith('/') ? (
    <Image bare url={icon} alt="" />
  ) : (
    icon
  );

export const QvpBanner: React.FC<QvpBannerProps> = ({
  variant = 'full-width',
  text,
  columnCount = 3,
  background,
  heading,
  inlineStat,
  column1Stat, column1Text, column1Icon, column1StatLabel, column1IconLabel,
  column2Stat, column2Text, column2Icon, column2StatLabel, column2IconLabel,
  column3Stat, column3Text, column3Icon, column3StatLabel, column3IconLabel,
  column4Stat, column4Text, column4Icon, column4StatLabel, column4IconLabel,
  column5Stat, column5Text, column5Icon, column5StatLabel, column5IconLabel,
  ariaLabel,
  anchorId,
}) => {
  const resolvedAriaLabel = ariaLabel || DEFAULT_ARIA_LABEL;
  const resolvedBackgroundColor = getBackgroundColor(background);

  // Reassemble columns from flat props
  const columns = [
    { stat: column1Stat, text: column1Text, icon: column1Icon, statLabel: column1StatLabel, iconLabel: column1IconLabel },
    { stat: column2Stat, text: column2Text, icon: column2Icon, statLabel: column2StatLabel, iconLabel: column2IconLabel },
    { stat: column3Stat, text: column3Text, icon: column3Icon, statLabel: column3StatLabel, iconLabel: column3IconLabel },
    { stat: column4Stat, text: column4Text, icon: column4Icon, statLabel: column4StatLabel, iconLabel: column4IconLabel },
    { stat: column5Stat, text: column5Text, icon: column5Icon, statLabel: column5StatLabel, iconLabel: column5IconLabel },
  ].filter((c): c is { stat: string | undefined; text: string; icon: string | undefined; statLabel: string | undefined; iconLabel: string | undefined } => !!c.text);

  // Full width variant — text directly inside a 1264 max-width wrapper
  if (variant === 'full-width') {
    return (
      <section
        {...blockAnchorAttributes(anchorId)}
        className="qvp-banner qvp-banner--full-width"
        aria-label={resolvedAriaLabel}
        role="region"
        style={{ background: resolvedBackgroundColor }}
      >
        <div className="qvp-banner__wrapper">
          {renderQvpText(text!, 'qvp-text-large')}
        </div>
      </section>
    );
  }

  // Container variant — text inside an off-white rounded pill, nested in the wrapper.
  // Outer section is always full-width white — background prop is ignored for the section.
  if (variant === 'container') {
    return (
      <section
        {...blockAnchorAttributes(anchorId)}
        className="qvp-banner qvp-banner--container"
        aria-label={resolvedAriaLabel}
        role="region"
      >
        <div className="qvp-banner__wrapper">
          <div className="qvp-banner__pill">
            {renderQvpText(text!, 'qvp-text-large')}
          </div>
        </div>
      </section>
    );
  }

  // Inline stat variant — large orange number inline with text (e.g. "300M+ Unique visitors per year")
  if (variant === 'inline-stat') {
    // Skip rendering when both inline pieces are missing — an empty
    // <section role="region"> is flagged by axe as a meaningless ARIA
    // landmark, and there's no visible content to anchor it to.
    if (!inlineStat && !text) return null;

    return (
      <section
        {...blockAnchorAttributes(anchorId)}
        className="qvp-banner qvp-banner--inline-stat"
        aria-label={resolvedAriaLabel}
        role="region"
        style={{ background: resolvedBackgroundColor }}
      >
        <div className="qvp-banner__wrapper">
          <p className="qvp-inline-stat">
            {inlineStat && <span className="qvp-inline-stat__number">{inlineStat}</span>}
            {text && (
              <span className="qvp-inline-stat__text">
                <ReactMarkdown components={RICH_TEXT_COMPONENTS}>{text}</ReactMarkdown>
              </span>
            )}
          </p>
        </div>
      </section>
    );
  }

  // Multi-column variant — native CSS Grid with breakpoint-driven 2-up at ≤767.
  // We use CSS Grid (not Cloudscape <Grid>) because the design calls for a
  // 2×2 grid at small breakpoints regardless of columnCount, which Cloudscape's
  // 12-column system can't express cleanly.
  const visibleColumns = columns.slice(0, columnCount);

  return (
    <section
      {...blockAnchorAttributes(anchorId)}
      className={`qvp-banner qvp-banner--multi-column qvp-banner--${columnCount}col`}
      aria-label={resolvedAriaLabel}
      role="region"
      style={{ background: resolvedBackgroundColor }}
    >
      <div className="qvp-banner__wrapper">
        {heading && <h2 className="qvp-banner__heading">{heading}</h2>}
        <div className="qvp-banner__grid">
          {visibleColumns.map((column, index) => (
            <div key={index} className="qvp-banner__column">
              {column.icon && (
                column.iconLabel ? (
                  <div
                    className="qvp-banner__icon"
                    role="img"
                    aria-label={column.iconLabel}
                  >
                    {renderColumnIcon(column.icon)}
                  </div>
                ) : (
                  <div className="qvp-banner__icon" aria-hidden="true">
                    {renderColumnIcon(column.icon)}
                  </div>
                )
              )}
              {column.stat && (
                <div
                  className="qvp-banner__stat"
                  aria-label={column.statLabel || `Statistic: ${column.stat}`}
                  role="text"
                >
                  {column.stat}
                </div>
              )}
              {renderQvpText(column.text, 'qvp-banner__column-text')}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
